<template>
  <div
    class="flex flex-col items-center justify-center w-full pb-20 bg-[#F2F6FA]"
  >
    <!-- Full Page Loader -->
    <!-- Removed common loader - using section-specific loaders instead -->

    <section class="flex flex-col items-center w-full justify-center">
      <div
        class="flex items-center justify-start md:mt-5 mb-2 md:mb-0 mt-2 px-2 md:px-0 w-full"
      >
        <div class="items-center gap-2 px-4 mt-5 -ml-2 d-flex">
          <img
            src="@/assets/img/sidebar/content_n.svg"
            class="cursor-pointer w-6 md:w-[28px]"
            alt="post"
          />
          <div class="flex items-center gap-1">
            <p class="text-base md:text-[22px] font-bold">
              Google Search Insights
            </p>
          </div>
        </div>
        <!-- Add Download Snapshot Button -->
        <div class="ml-auto mr-4 mt-5">
          <v-btn
            small
            color="primary"
            class="px-2"
            @click="downloadSnapshot"
            :loading="isDownloading"
            :disabled="!summaryData"
          >
            <v-icon left small>mdi-camera</v-icon>
            Snapshot
          </v-btn>
        </div>
      </div>
      <div
        class="flex items-center justify-start mb-3 ml-[48px] md:mb-0 px-2 md:px-0 w-full"
      >
        <div class="items-center gap-2 px-4 d-flex">
          <div class="flex items-center gap-1">
            <p class="text-xs md:text-[18px] text-gray-600">
              See traffic by keywords for your locations from Google
            </p>
          </div>
        </div>
      </div>
      <div class="w-full mx-auto mt-3">
        <div class="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6 min-h-[80vh]">
          <div class="w-full mx-auto -mt-5">
            <!-- Add Tab Navigation -->
            <v-tabs
              v-model="activeTab"
              background-color="transparent"
              color="primary"
              grow
            >
              <v-tab>Dashboard</v-tab>
              <v-tab>Search Report</v-tab>
              <v-tab>Recommended Keywords</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab" class="w-full">
              <!-- Dashboard Tab -->
              <v-tab-item>
                <!-- Dashboard Loading State -->
                <div v-if="isRefreshingData" class="text-center py-12">
                  <div class="flex flex-col items-center justify-center">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="48"
                      class="mb-4 loading-spin"
                    ></v-progress-circular>
                    <p class="text-lg font-medium text-gray-700 mb-2">
                      Refreshing Data...
                    </p>
                    <p class="text-sm text-gray-500 loading-dots">
                      Updating your dashboard with the latest insights
                    </p>
                  </div>
                </div>
                <div v-else-if="stores.length === 0" class="text-center py-8">
                  <div
                    class="p-8 bg-white rounded-lg shadow-sm max-w-3xl mx-auto text-center"
                  >
                    <NoLocationsConnected
                      message="Connect your Google Business locations with Fly to access search insights. Once connected, you'll see what search terms customers use to find your business on Google."
                      actionText="Connect Google Locations"
                      @click.native="handleConnectGMB"
                    />
                  </div>
                </div>
                <div v-else>
                  <!-- Dashboard Content -->
                  <div class="space-y-6">
                    <!-- Time Period Selector -->
                    <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
                      <div
                        class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
                      >
                        <div class="flex items-center">
                          <v-icon color="primary" class="mr-2"
                            >mdi-calendar-range</v-icon
                          >
                          <h3 class="text-lg font-semibold">Time Period</h3>
                          <!-- Subtle loading indicator for time range updates -->
                          <div
                            v-if="isUpdatingTimeRange"
                            class="ml-2 flex items-center"
                          >
                            <v-progress-circular
                              indeterminate
                              color="primary"
                              size="16"
                              class="mr-2"
                            ></v-progress-circular>
                            <span class="text-sm text-gray-500"
                              >Updating...</span
                            >
                          </div>
                        </div>
                        <div class="flex flex-col gap-3 w-full md:w-auto">
                          <!-- Quick Range Selector -->
                          <div class="flex flex-wrap gap-2">
                            <v-btn-toggle
                              v-model="selectedTimeRange"
                              mandatory
                              class="elevation-0 rounded-lg"
                              @change="handleTimeRangeChange"
                              :disabled="
                                isRefreshingData || isUpdatingTimeRange
                              "
                            >
                              <v-btn
                                value="3m"
                                class="text-xs px-4 py-2 rounded-md"
                                >Last 3 Months</v-btn
                              >
                              <v-btn
                                value="6m"
                                class="text-xs px-4 py-2 rounded-md"
                                >Last 6 Months</v-btn
                              >
                              <v-btn
                                value="12m"
                                class="text-xs px-4 py-2 rounded-md"
                                >Last 12 Months</v-btn
                              >
                              <v-btn
                                value="custom"
                                class="text-xs px-4 py-2 rounded-md"
                                >Custom</v-btn
                              >
                            </v-btn-toggle>
                          </div>

                          <!-- Custom Months Selector -->
                          <div
                            v-if="selectedTimeRange === 'custom'"
                            class="flex flex-col gap-2"
                          >
                            <div class="flex items-center gap-2">
                              <v-icon small color="primary"
                                >mdi-calendar-multiple</v-icon
                              >
                              <span class="text-sm font-medium"
                                >Select Months:</span
                              >
                            </div>
                            <div class="flex flex-wrap gap-2 max-w-md">
                              <v-chip
                                v-for="month in availableMonths"
                                :key="month.value"
                                :color="
                                  selectedMonths.includes(month.value)
                                    ? 'primary'
                                    : 'grey lighten-3'
                                "
                                :text-color="
                                  selectedMonths.includes(month.value)
                                    ? 'white'
                                    : 'grey darken-3'
                                "
                                @click="toggleMonth(month.value)"
                                class="cursor-pointer text-xs"
                                small
                              >
                                {{ month.text }}
                              </v-chip>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Summary Cards -->
                    <div
                      v-if="loadingSummaryCards"
                      class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
                    >
                      <div
                        v-for="n in 4"
                        :key="n"
                        class="bg-white rounded-lg shadow-sm p-4"
                      >
                        <v-skeleton-loader
                          type="card-heading, text"
                          class="mb-2"
                        ></v-skeleton-loader>
                        <v-skeleton-loader
                          type="heading"
                          class="w-32"
                        ></v-skeleton-loader>
                      </div>
                    </div>
                    <div
                      v-else-if="summaryData"
                      class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
                    >
                      <div
                        class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
                      >
                        <h3 class="text-sm font-medium text-gray-500 mb-2">
                          Total Brand Traffic
                        </h3>
                        <div class="flex items-center justify-between">
                          <p class="text-2xl font-bold text-primary">
                            {{
                              formatNumber(summaryData?.totalBrandTraffic || 0)
                            }}
                          </p>
                          <div class="w-20 h-8 relative group">
                            <canvas ref="totalTrafficTrend"></canvas>
                            <div
                              class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded"
                            ></div>
                            <div
                              class="absolute left-0 top-0 w-56 bg-white shadow-lg rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 pointer-events-none border"
                            >
                              <div class="text-xs font-medium mb-2">
                                Total Brand Traffic
                              </div>
                              <div
                                v-for="(
                                  value, month
                                ) in getTotalTrafficMonthlyData()"
                                :key="month"
                                class="text-xs flex justify-between items-center py-1"
                              >
                                <span class="text-gray-600"
                                  >{{ formatMonth(month) }}:</span
                                >
                                <div class="flex items-center">
                                  <span class="ml-1 font-medium">{{
                                    formatNumber(value.traffic)
                                  }}</span>
                                  <span
                                    v-if="value.difference !== null"
                                    class="ml-2 text-xs px-1 py-0.5 rounded"
                                    :class="
                                      value.difference > 0
                                        ? 'bg-green-100 text-green-800'
                                        : value.difference < 0
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-gray-100 text-gray-800'
                                    "
                                  >
                                    {{ value.difference > 0 ? "+" : ""
                                    }}{{ value.difference }}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
                      >
                        <h3 class="text-sm font-medium text-gray-500 mb-2">
                          Total Keywords
                        </h3>
                        <p class="text-2xl font-bold text-primary">
                          {{ formatNumber(getTotalKeywords() || 0) }}
                        </p>
                      </div>
                      <div
                        class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
                      >
                        <h3 class="text-sm font-medium text-gray-500 mb-2">
                          Active Locations
                        </h3>
                        <p class="text-2xl font-bold text-primary">
                          {{ summaryData?.storeCount || 0 }}
                        </p>
                      </div>
                      <div
                        class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
                      >
                        <h3 class="text-sm font-medium text-gray-500 mb-2">
                          Last Updated
                        </h3>
                        <p class="text-lg font-semibold text-primary">
                          {{ formatLastUpdated(summaryData?.lastUpdated) }}
                        </p>
                      </div>
                    </div>

                    <!-- Category Distribution Chart -->
                    <div
                      v-if="loadingCategoryDistribution"
                      class="bg-white rounded-lg shadow-sm p-6 mb-6"
                    >
                      <h3 class="text-lg font-semibold mb-4">
                        Keyword Category Distribution
                      </h3>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div class="h-[300px] flex items-center justify-center">
                          <v-progress-circular
                            indeterminate
                            color="primary"
                            size="48"
                          ></v-progress-circular>
                        </div>
                        <div class="space-y-4">
                          <v-skeleton-loader
                            v-for="n in 4"
                            :key="n"
                            type="list-item-two-line"
                          ></v-skeleton-loader>
                        </div>
                      </div>
                    </div>
                    <div
                      v-else-if="!summaryData?.categoryDistribution?.length"
                      class="bg-white rounded-lg shadow-sm p-6 mb-6"
                    >
                      <h3 class="text-lg font-semibold mb-4">
                        Keyword Category Distribution
                      </h3>
                      <div class="text-center py-8">
                        <v-icon color="grey lighten-1" size="48" class="mb-4"
                          >mdi-chart-pie</v-icon
                        >
                        <p class="text-gray-500">
                          No category distribution data available
                        </p>
                      </div>
                    </div>
                    <div v-else class="bg-white rounded-lg shadow-sm p-6 mb-6">
                      <h3 class="text-lg font-semibold mb-4">
                        Keyword Category Distribution
                      </h3>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Pie Chart -->
                        <div class="h-[300px]">
                          <canvas ref="categoryChart"></canvas>
                        </div>
                        <!-- Category Stats -->
                        <div class="space-y-4">
                          <div
                            v-for="category in orderedCategoryDistribution"
                            :key="category.category"
                            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div class="flex items-center flex-1">
                              <v-icon small class="mr-2">{{
                                getCategoryIcon(category.category)
                              }}</v-icon>
                              <span class="font-medium capitalize">{{
                                category.category
                              }}</span>
                            </div>
                            <div class="flex items-center gap-4">
                              <div class="w-20 h-8 relative group">
                                <canvas
                                  :ref="
                                    'categoryTrend_' +
                                    getCategoryIndex(category.category)
                                  "
                                ></canvas>
                                <div
                                  class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded"
                                ></div>
                                <div
                                  class="absolute left-0 top-0 w-56 bg-white shadow-lg rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 pointer-events-none border bg-opacity-95 backdrop-blur-sm"
                                >
                                  <div class="text-xs font-medium mb-2">
                                    {{ category.category }}
                                  </div>
                                  <div
                                    v-for="(
                                      value, month
                                    ) in getCategoryMonthlyDataWithDifference(
                                      category.category
                                    )"
                                    :key="month"
                                    class="text-xs flex justify-between items-center py-1"
                                  >
                                    <span class="text-gray-600"
                                      >{{ formatMonth(month) }}:</span
                                    >
                                    <div class="flex items-center">
                                      <span class="ml-1 font-medium">{{
                                        formatNumber(value.traffic)
                                      }}</span>
                                      <span
                                        v-if="value.difference !== null"
                                        class="ml-2 text-xs px-1 py-0.5 rounded"
                                        :class="
                                          value.difference > 0
                                            ? 'bg-green-100 text-green-800'
                                            : value.difference < 0
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-gray-100 text-gray-800'
                                        "
                                      >
                                        {{ value.difference > 0 ? "+" : ""
                                        }}{{ value.difference }}%
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="text-right min-w-[80px]">
                                <p class="text-lg font-bold">
                                  {{ formatNumber(category.totalTraffic) }}
                                </p>
                                <p class="text-sm text-gray-500">
                                  {{ category.percentage.toFixed(1) }}%
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Top Keywords by Category -->
                    <div
                      v-if="loadingTopKeywords"
                      class="bg-white rounded-lg shadow-sm p-6 mb-6"
                    >
                      <h3 class="text-lg font-semibold mb-4">
                        Top Keywords by Category
                      </h3>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div
                          v-for="n in 4"
                          :key="n"
                          class="bg-gray-50 rounded-lg p-4"
                        >
                          <v-skeleton-loader
                            type="list-item-avatar-two-line"
                            class="mb-4"
                          ></v-skeleton-loader>
                          <v-skeleton-loader
                            type="list-item"
                            class="mb-2"
                          ></v-skeleton-loader>
                          <v-skeleton-loader
                            type="list-item"
                            class="mb-2"
                          ></v-skeleton-loader>
                          <v-skeleton-loader
                            type="list-item"
                          ></v-skeleton-loader>
                        </div>
                      </div>
                    </div>
                    <div
                      v-else-if="!summaryData?.categoryWiseData"
                      class="bg-white rounded-lg shadow-sm p-6 mb-6"
                    >
                      <h3 class="text-lg font-semibold mb-4">
                        Top Keywords by Category
                      </h3>
                      <div class="text-center py-8">
                        <v-icon color="grey lighten-1" size="48" class="mb-4"
                          >mdi-text-search</v-icon
                        >
                        <p class="text-gray-500">
                          No keyword data available by category
                        </p>
                      </div>
                    </div>
                    <div v-else class="bg-white rounded-lg shadow-sm p-6 mb-6">
                      <h3 class="text-lg font-semibold mb-4">
                        Top Keywords by Category
                      </h3>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div
                          v-for="category in orderedCategories"
                          :key="category"
                          v-if="summaryData?.categoryWiseData[category]"
                          class="bg-gray-50 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
                        >
                          <div
                            class="flex items-center justify-between mb-4 pb-3 border-b border-gray-200"
                          >
                            <div class="flex items-center">
                              <v-icon
                                small
                                class="mr-2"
                                :color="getCategoryColor(category)"
                                >{{ getCategoryIcon(category) }}</v-icon
                              >
                              <h4 class="text-md font-semibold capitalize">
                                {{ category }}
                              </h4>
                            </div>
                            <span
                              class="text-sm font-bold"
                              :class="getCategoryTextColor(category)"
                            >
                              {{ getCategoryPercentage(category).toFixed(1) }}%
                            </span>
                          </div>
                          <div
                            v-if="
                              !summaryData?.categoryWiseData[category]?.keywords
                                ?.length
                            "
                            class="text-center py-4"
                          >
                            <p class="text-gray-500">
                              No keywords in this category
                            </p>
                          </div>
                          <div v-else class="space-y-3">
                            <div
                              class="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar"
                            >
                              <div
                                v-for="(keyword, index) in summaryData
                                  ?.categoryWiseData[category]?.keywords"
                                :key="index"
                                class="flex items-center justify-between p-3 bg-white rounded-lg mb-2 hover:shadow-sm transition-all duration-200"
                              >
                                <div class="flex-1 mr-4">
                                  <div
                                    class="font-medium break-words text-sm"
                                    :title="keyword.keyword"
                                  >
                                    {{ keyword.keyword }}
                                  </div>
                                  <div class="text-xs text-gray-500 mt-1">
                                    Traffic: {{ formatNumber(keyword.traffic) }}
                                    <span
                                      class="ml-2 px-1 py-0.5 bg-blue-100 text-blue-800 rounded"
                                    >
                                      {{
                                        getTrafficPercentageFromTotal(
                                          keyword.traffic
                                        ).toFixed(1)
                                      }}% of total
                                    </span>
                                  </div>
                                </div>
                                <div class="flex items-center ml-2">
                                  <div class="w-20 h-8 relative group">
                                    <canvas
                                      :ref="
                                        'keywordTrend_' + category + '_' + index
                                      "
                                    ></canvas>
                                    <div
                                      class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded"
                                    ></div>
                                    <div
                                      class="absolute left-0 top-0 w-56 bg-white shadow-lg rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-[9999] pointer-events-none border bg-opacity-95 backdrop-blur-sm"
                                    >
                                      <div class="text-xs font-medium mb-2">
                                        {{ keyword.keyword }}
                                      </div>
                                      <div
                                        v-for="(
                                          value, month
                                        ) in getKeywordMonthlyDataWithDifference(
                                          keyword.keyword
                                        )"
                                        :key="month"
                                        class="text-xs flex justify-between items-center py-1"
                                      >
                                        <span class="text-gray-600"
                                          >{{ formatMonth(month) }}:</span
                                        >
                                        <div class="flex items-center">
                                          <span class="ml-1 font-medium">{{
                                            formatNumber(value.traffic)
                                          }}</span>
                                          <span class="ml-1 text-gray-500"
                                            >({{
                                              value.percentage.toFixed(1)
                                            }}%)</span
                                          >
                                          <span
                                            v-if="value.difference !== null"
                                            class="ml-2 text-xs px-1 py-0.5 rounded"
                                            :class="
                                              value.difference > 0
                                                ? 'bg-green-100 text-green-800'
                                                : value.difference < 0
                                                ? 'bg-red-100 text-red-800'
                                                : 'bg-gray-100 text-gray-800'
                                            "
                                          >
                                            {{ value.difference > 0 ? "+" : ""
                                            }}{{ value.difference }}%
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Keyword Summary -->
                    <div
                      v-if="loadingKeywordSummary"
                      class="bg-white rounded-lg shadow-sm p-6 mb-6"
                    >
                      <div
                        class="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4"
                      >
                        <v-skeleton-loader
                          type="list-item-avatar"
                        ></v-skeleton-loader>
                        <v-skeleton-loader
                          type="button"
                          class="w-32"
                        ></v-skeleton-loader>
                      </div>
                      <div class="overflow-x-auto -mx-6 sm:mx-0">
                        <v-skeleton-loader
                          type="table"
                          rows="5"
                        ></v-skeleton-loader>
                      </div>
                    </div>
                    <div
                      v-else-if="summaryData"
                      class="bg-white rounded-lg shadow-sm p-6 mb-6"
                    >
                      <div
                        class="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4"
                      >
                        <div class="flex items-center">
                          <v-icon color="primary" class="mr-2"
                            >mdi-text-search</v-icon
                          >
                          <h3 class="text-lg font-semibold">Keyword Summary</h3>
                        </div>
                        <div
                          class="flex flex-col md:flex-row gap-4 w-full md:w-auto"
                        >
                          <!-- Category Filters -->
                          <div class="flex flex-wrap gap-2">
                            <v-chip
                              v-for="category in [
                                'branded',
                                'navigational',
                                'transactional',
                                'informational',
                              ]"
                              :key="category"
                              :color="
                                selectedCategories.includes(category)
                                  ? 'primary'
                                  : 'grey lighten-3'
                              "
                              :text-color="
                                selectedCategories.includes(category)
                                  ? 'white'
                                  : 'grey darken-3'
                              "
                              @click="toggleCategory(category)"
                              class="cursor-pointer"
                            >
                              <v-icon left small>{{
                                getCategoryIcon(category)
                              }}</v-icon>
                              {{
                                category.charAt(0).toUpperCase() +
                                category.slice(1)
                              }}
                            </v-chip>
                          </div>
                          <!-- Trend Filters -->
                          <div class="flex flex-wrap gap-2">
                            <v-chip
                              v-for="trend in ['Rising', 'Falling', 'Holding']"
                              :key="trend"
                              :color="
                                selectedTrends.includes(trend)
                                  ? 'primary'
                                  : 'grey lighten-3'
                              "
                              :text-color="
                                selectedTrends.includes(trend)
                                  ? 'white'
                                  : 'grey darken-3'
                              "
                              @click="toggleTrend(trend)"
                              class="cursor-pointer"
                            >
                              <v-icon left small>{{
                                getTrendIcon(trend)
                              }}</v-icon>
                              {{ trend }}
                            </v-chip>
                          </div>
                          <!-- Search Field -->
                          <v-text-field
                            v-model="keywordSearch"
                            prepend-inner-icon="mdi-magnify"
                            label="Search keywords"
                            hide-details
                            dense
                            outlined
                            class="max-w-xs"
                          ></v-text-field>
                          <!-- Download Button -->
                          <v-btn
                            color="primary"
                            outlined
                            small
                            @click="downloadKeywordSummary"
                            :disabled="!filteredBrandKeywords.length"
                          >
                            <v-icon left small>mdi-download</v-icon>
                            Download CSV
                          </v-btn>
                        </div>
                      </div>
                      <div class="overflow-x-auto -mx-6 sm:mx-0">
                        <div class="inline-block min-w-full align-middle">
                          <div class="overflow-hidden">
                            <table class="min-w-full divide-y divide-gray-200">
                              <thead class="bg-gray-50">
                                <tr>
                                  <th
                                    scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    Keyword
                                  </th>
                                  <th
                                    scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                  >
                                    Category
                                  </th>
                                  <th
                                    scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                  >
                                    Trend
                                  </th>
                                  <th
                                    scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer"
                                    @click="sortBrandKeywords('traffic')"
                                  >
                                    Traffic
                                    <v-icon small>{{
                                      sortDirection === "asc" &&
                                      sortColumn === "traffic"
                                        ? "mdi-arrow-up"
                                        : "mdi-arrow-down"
                                    }}</v-icon>
                                  </th>
                                  <th
                                    scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer"
                                    @click="
                                      sortBrandKeywords('trafficPercentage')
                                    "
                                  >
                                    Traffic %
                                    <v-icon small>{{
                                      sortDirection === "asc" &&
                                      sortColumn === "trafficPercentage"
                                        ? "mdi-arrow-up"
                                        : "mdi-arrow-down"
                                    }}</v-icon>
                                  </th>
                                </tr>
                              </thead>
                              <tbody class="bg-white divide-y divide-gray-200">
                                <tr
                                  v-for="(
                                    keyword, index
                                  ) in filteredBrandKeywords"
                                  :key="keyword.keyword"
                                  class="hover:bg-gray-50 transition-colors duration-150"
                                >
                                  <td class="px-6 py-4">
                                    <div
                                      class="text-sm font-medium text-gray-900 break-words"
                                      :title="keyword.keyword"
                                    >
                                      {{ keyword.keyword }}
                                    </div>
                                  </td>
                                  <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                      class="px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 capitalize"
                                      :class="
                                        getCategoryColorClass(keyword.category)
                                      "
                                    >
                                      <v-icon x-small>{{
                                        getCategoryIcon(keyword.category)
                                      }}</v-icon>
                                      {{ keyword.category }}
                                    </span>
                                  </td>
                                  <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                      <div class="w-20 h-8 relative group mr-2">
                                        <canvas
                                          :ref="'brandKeywordTrend_' + index"
                                        ></canvas>
                                        <div
                                          class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded"
                                        ></div>
                                        <div
                                          class="absolute left-0 top-0 w-56 bg-white shadow-lg rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 pointer-events-none border bg-opacity-95 backdrop-blur-sm"
                                        >
                                          <div class="text-xs font-medium mb-2">
                                            {{ keyword.keyword }}
                                          </div>
                                          <div
                                            v-for="(
                                              value, month
                                            ) in getKeywordMonthlyDataWithDifference(
                                              keyword.keyword
                                            )"
                                            :key="month"
                                            class="text-xs flex justify-between items-center py-1"
                                          >
                                            <span class="text-gray-600"
                                              >{{ formatMonth(month) }}:</span
                                            >
                                            <div class="flex items-center">
                                              <span class="ml-1 font-medium">{{
                                                formatNumber(value.traffic)
                                              }}</span>
                                              <span class="ml-1 text-gray-500"
                                                >({{
                                                  value.percentage.toFixed(1)
                                                }}%)</span
                                              >
                                              <span
                                                v-if="value.difference !== null"
                                                class="ml-2 text-xs px-1 py-0.5 rounded"
                                                :class="
                                                  value.difference > 0
                                                    ? 'bg-green-100 text-green-800'
                                                    : value.difference < 0
                                                    ? 'bg-red-100 text-red-800'
                                                    : 'bg-gray-100 text-gray-800'
                                                "
                                              >
                                                {{
                                                  value.difference > 0
                                                    ? "+"
                                                    : ""
                                                }}{{ value.difference }}%
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <span
                                        class="text-xs font-medium px-2 py-1 rounded-full"
                                        :class="
                                          getTrendCategory(keyword.keyword) ===
                                          'Rising'
                                            ? 'bg-green-100 text-green-800'
                                            : getTrendCategory(
                                                keyword.keyword
                                              ) === 'Falling'
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-gray-100 text-gray-800'
                                        "
                                      >
                                        {{ getTrendCategory(keyword.keyword) }}
                                      </span>
                                    </div>
                                  </td>
                                  <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900">
                                      {{ formatNumber(keyword.traffic) }}
                                    </div>
                                  </td>
                                  <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                      <div
                                        class="w-24 bg-gray-200 rounded-full h-2 mr-2"
                                      >
                                        <div
                                          class="h-2 rounded-full"
                                          :class="
                                            getTrafficPercentageClass(
                                              getTrafficPercentageFromTotal(
                                                keyword.traffic
                                              )
                                            )
                                          "
                                          :style="{
                                            width: `${Math.min(
                                              getTrafficPercentageFromTotal(
                                                keyword.traffic
                                              ),
                                              100
                                            )}%`,
                                          }"
                                        ></div>
                                      </div>
                                      <span class="text-sm text-gray-900"
                                        >{{
                                          getTrafficPercentageFromTotal(
                                            keyword.traffic
                                          ).toFixed(1)
                                        }}%</span
                                      >
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- No Data State -->
                    <div v-if="!summaryData" class="text-center py-12">
                      <div
                        class="p-8 bg-gray-50 rounded-lg max-w-3xl mx-auto text-center border border-gray-200"
                      >
                        <v-icon class="mb-4" color="primary" size="48"
                          >mdi-chart-line</v-icon
                        >
                        <h3 class="text-lg font-semibold mb-2">
                          No Search Insights Data Available
                        </h3>
                        <p class="text-gray-600 mb-4">
                          We haven't collected any search insights data yet.
                          This could be because:
                        </p>
                        <ul
                          class="text-left text-gray-600 mb-6 max-w-md mx-auto"
                        >
                          <li class="flex items-center mb-2">
                            <v-icon small color="info" class="mr-2"
                              >mdi-clock-outline</v-icon
                            >
                            Data collection is still in progress
                          </li>
                          <li class="flex items-center mb-2">
                            <v-icon small color="info" class="mr-2"
                              >mdi-store-outline</v-icon
                            >
                            No locations are connected yet
                          </li>
                          <li class="flex items-center">
                            <v-icon small color="info" class="mr-2"
                              >mdi-sync</v-icon
                            >
                            Data needs to be refreshed
                          </li>
                        </ul>
                        <v-btn
                          color="primary"
                          @click="fetchSummaryData(false)"
                          :loading="isRefreshingData"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Refresh Data
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>
              </v-tab-item>

              <!-- Search Report Tab -->
              <v-tab-item>
                <!-- Search Report Loading State -->
                <div v-if="isGenerating" class="text-center py-12">
                  <div class="flex flex-col items-center justify-center">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="48"
                      class="mb-4 loading-spin"
                    ></v-progress-circular>
                    <p class="text-lg font-medium text-gray-700 mb-2">
                      Generating Search Report
                    </p>
                    <p class="text-sm text-gray-500 loading-dots">
                      Please wait while we analyze your search data...
                    </p>
                  </div>
                </div>
                <div v-else-if="stores.length === 0" class="text-center py-8">
                  <div
                    class="p-8 bg-white rounded-lg shadow-sm max-w-3xl mx-auto text-center"
                  >
                    <NoLocationsConnected
                      message="Connect your Google Business locations with Fly to access search insights. Once connected, you'll see what search terms customers use to find your business on Google."
                      actionText="Connect Google Locations"
                      @click.native="handleConnectGMB"
                    />
                  </div>
                </div>
                <div v-else>
                  <div
                    class="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full"
                  >
                    <!-- Location Dropdown -->
                    <div
                      class="relative w-full sm:w-auto flex-1 md:w-1/3 mr-2 max-w-[400px]"
                    >
                      <div @click="toggleStoreDropdown($event)">
                        <v-text-field
                          v-model="locationSearch"
                          :label="
                            selectedStore
                              ? selectedStore.sub_locality
                              : 'Select Location'
                          "
                          hide-details
                          dense
                          outlined
                          class="location-input min-w-[350px] mt-10"
                          :error="showValidation && !selectedStore"
                          color="rgba(0, 0, 0, 0.6)"
                          background-color="white"
                          prepend-inner-icon="mdi-map-marker"
                        >
                          <template v-slot:append>
                            <v-icon>{{
                              showDropdown
                                ? "mdi-chevron-up"
                                : "mdi-chevron-down"
                            }}</v-icon>
                          </template>
                        </v-text-field>
                      </div>
                      <div class="h-5">
                        <span
                          v-if="showValidation && !selectedStore"
                          class="text-red-500 text-xs"
                          >Please select a location</span
                        >
                      </div>
                      <div
                        ref="showStoreDropdown"
                        v-if="showDropdown"
                        class="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 max-h-60 min-w-[350px] max-w-[800px] overflow-y-auto py-2 px-2"
                      >
                        <div class="sticky top-0 bg-white p-2 mb-2 border-b">
                          <v-text-field
                            v-model="locationSearch"
                            label="Search locations"
                            hide-details
                            dense
                            outlined
                            prepend-inner-icon="mdi-magnify"
                            clearable
                            @click:clear="clearLocation"
                            class="mb-1"
                          ></v-text-field>
                        </div>
                        <div
                          v-for="profile in filteredStores"
                          :key="profile.wid"
                          class="platform p-3 hover:bg-gray-100 cursor-pointer rounded-md mb-1"
                          @click="selectStore(profile)"
                          :class="{
                            'bg-blue-50 border border-blue-200':
                              selectedWID === profile.wid,
                          }"
                        >
                          <div class="flex items-center gap-2">
                            <v-avatar size="32" color="grey lighten-3">
                              <v-icon color="grey darken-2">mdi-store</v-icon>
                            </v-avatar>
                            <div class="flex flex-col w-full">
                              <span
                                class="font-medium text-base"
                                v-if="profile.gmbProfile?.title"
                              >
                                {{ profile.gmbProfile?.title }}
                              </span>
                              <span
                                class="text-xs text-gray-600"
                                v-if="profile.sub_locality"
                              >
                                {{ profile.sub_locality }}
                              </span>
                              <span class="text-[11px] text-gray-500">{{
                                profile.city
                              }}</span>
                            </div>
                          </div>
                        </div>
                        <div
                          v-if="filteredStores.length === 0"
                          class="p-3 text-center text-gray-500"
                        >
                          No locations match your search
                        </div>
                      </div>
                    </div>

                    <!-- Date Range Selector -->
                    <div class="sm:w-auto flex-1 md:w-1/3 mr-2">
                      <v-menu
                        v-model="datePickerMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="dateRangeDisplay"
                            label="Date Range"
                            prepend-inner-icon="mdi-calendar-range"
                            readonly
                            outlined
                            dense
                            hide-details
                            class="date-range-input mt-10"
                            :error="
                              showValidation &&
                              (!selectedStartMonthYear || !selectedEndMonthYear)
                            "
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-card min-width="300" class="pa-4">
                          <div
                            class="d-flex justify-space-between align-center mb-4"
                          >
                            <v-subheader>Select Time Period</v-subheader>
                            <v-btn
                              text
                              small
                              color="primary"
                              @click="applyPresetRange('last3Months')"
                              >Last 3 Months</v-btn
                            >
                            <v-btn
                              text
                              small
                              color="primary"
                              @click="applyPresetRange('last6Months')"
                              >Last 6 Months</v-btn
                            >
                          </div>

                          <div class="d-flex justify-space-between mb-4 mt-4">
                            <v-select
                              v-model="selectedStartMonthYear"
                              :items="startMonthYearOptions"
                              label="Start Month"
                              outlined
                              dense
                              hide-details
                              class="mr-2"
                              :disabled="isGenerating"
                            ></v-select>

                            <v-select
                              v-model="selectedEndMonthYear"
                              :items="endMonthYearOptions"
                              label="End Month"
                              outlined
                              dense
                              hide-details
                              :disabled="isGenerating"
                            ></v-select>
                          </div>

                          <div class="d-flex justify-end mt-4">
                            <v-btn text @click="datePickerMenu = false"
                              >Cancel</v-btn
                            >
                            <v-btn color="primary" @click="applyDateRange"
                              >Apply</v-btn
                            >
                          </div>
                        </v-card>
                      </v-menu>
                      <div class="h-5">
                        <span
                          v-if="
                            showValidation &&
                            (!selectedStartMonthYear || !selectedEndMonthYear)
                          "
                          class="text-red-500 text-xs"
                        >
                          Please select date range
                        </span>
                      </div>
                    </div>

                    <!-- Generate Report Button -->
                    <div class="flex justify-end">
                      <v-btn
                        color="primary"
                        @click="generateReport"
                        :loading="isGenerating"
                        class="px-6 h-[40px] w-full sm:w-auto mt-5"
                      >
                        <v-icon left>mdi-file-chart</v-icon>
                        Generate Report
                      </v-btn>
                    </div>
                  </div>

                  <!-- Show guidance message when no data has been generated yet -->
                  <div
                    v-if="!hasData && !isGenerating"
                    class="text-center py-8 mt-6"
                  >
                    <div
                      class="p-6 bg-gray-50 rounded-lg max-w-3xl mx-auto text-center border border-gray-200"
                    >
                      <v-icon class="mb-4" color="primary" size="48"
                        >mdi-magnify</v-icon
                      >
                      <h3 class="text-lg font-semibold mb-2">
                        Ready to Generate Search Insights
                      </h3>
                      <p class="text-gray-600 mb-4">
                        Select a location and time period above, then click
                        "Generate Report" to see what search terms customers use
                        to find your business on Google.
                      </p>
                      <v-icon color="amber darken-2" small
                        >mdi-information-outline</v-icon
                      >
                      <span class="text-sm text-amber-600"
                        >This report shows the keywords people used to find your
                        business on Google Search and Maps.</span
                      >
                    </div>
                  </div>

                  <div
                    v-if="lastGeneratedAt"
                    class="text-center flex justify-end mb-4"
                  >
                    <p class="text-sm text-gray-500">
                      Generated {{ lastGeneratedAt }}
                    </p>
                  </div>

                  <!-- Report Content -->
                  <div v-if="hasData" class="space-y-6">
                    <!-- Download CSV Button and Volume Filters -->
                    <div class="flex justify-between items-center mb-4">
                      <div class="flex space-x-2">
                        <v-btn-toggle
                          v-model="volumeFilter"
                          mandatory
                          class="elevation-0 rounded-lg px-2 py-1"
                          style="gap: 8px"
                        >
                          <v-btn
                            small
                            value="all"
                            class="text-xs px-4 py-2 rounded-md"
                            >All</v-btn
                          >
                          <v-btn
                            small
                            value="high"
                            class="text-xs px-4 py-2 rounded-md"
                            >High Volume</v-btn
                          >
                          <v-btn
                            small
                            value="medium"
                            class="text-xs px-4 py-2 rounded-md"
                            >Medium Volume</v-btn
                          >
                          <v-btn
                            small
                            value="low"
                            class="text-xs px-4 py-2 rounded-md"
                            >Low Volume</v-btn
                          >
                        </v-btn-toggle>
                      </div>
                      <v-btn
                        color="primary"
                        icon
                        @click="downloadCSV"
                        title="Download as CSV"
                        :disabled="!hasData"
                      >
                        <v-icon>mdi-download-box-outline</v-icon>
                      </v-btn>
                    </div>

                    <!-- Keywords Visualization - Horizontal Bar Chart -->
                    <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
                      <h3 class="text-lg font-semibold mb-4">
                        Top Search Terms by Volume
                      </h3>
                      <div
                        v-if="filteredReportData.length === 0"
                        class="text-center py-8"
                      >
                        <p class="text-gray-500">
                          No keywords match the selected filter criteria
                        </p>
                      </div>
                      <div v-else class="keywords-bars-container">
                        <!-- Limited to top 10 for better visualization -->
                        <div
                          v-for="(item, index) in topKeywords"
                          :key="index"
                          class="keyword-bar-item mb-3"
                        >
                          <div class="flex items-center mb-1 justify-between">
                            <div class="flex items-center">
                              <div class="keyword-rank mr-2">
                                {{ index + 1 }}
                              </div>
                              <span
                                class="font-medium truncate max-w-[250px]"
                                :title="item.searchKeyword"
                              >
                                {{ item.searchKeyword }}
                              </span>
                            </div>
                            <div class="flex items-center">
                              <span
                                class="px-2 py-1 text-xs font-medium rounded-full mr-2"
                                :class="
                                  getVolumeTagClass(
                                    item.insightsValue.value ||
                                      item.insightsValue.threshold
                                  )
                                "
                              >
                                {{
                                  getVolumeLabel(
                                    item.insightsValue.value ||
                                      item.insightsValue.threshold
                                  )
                                }}
                              </span>
                              <span class="text-sm font-bold">
                                {{
                                  formatImpressions(
                                    item.insightsValue.value ||
                                      item.insightsValue.threshold
                                  )
                                }}
                              </span>
                            </div>
                          </div>
                          <div
                            class="relative bg-gray-200 h-6 rounded-md overflow-hidden w-full"
                          >
                            <div
                              class="absolute top-0 left-0 h-full rounded-md"
                              :class="
                                getBarColorClass(
                                  item.insightsValue.value ||
                                    item.insightsValue.threshold
                                )
                              "
                              :style="{
                                width:
                                  getBarWidthPercentage(
                                    item.insightsValue.value ||
                                      item.insightsValue.threshold
                                  ) + '%',
                              }"
                            ></div>
                          </div>
                        </div>

                        <!-- Show and hide more terms button if there are more than 10 -->
                        <div
                          v-if="filteredReportData.length > 10"
                          class="text-center mt-4"
                        >
                          <v-btn
                            text
                            color="primary"
                            @click="showAllKeywords = !showAllKeywords"
                          >
                            {{
                              showAllKeywords ? "Show Less" : "Show All Terms"
                            }}
                          </v-btn>
                        </div>
                      </div>
                    </div>

                    <!-- Table view -->
                    <div
                      class="bg-white rounded-lg shadow-sm overflow-hidden"
                      v-if="showAllKeywords || filteredReportData.length <= 10"
                    >
                      <h3 class="text-lg font-semibold p-4 border-b">
                        Keyword Details
                      </h3>
                      <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                          <thead class="bg-gray-50">
                            <tr>
                              <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                              >
                                Keyword
                              </th>
                              <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                              >
                                Impressions
                              </th>
                              <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                              >
                                Volume
                              </th>
                            </tr>
                          </thead>
                          <tbody class="bg-white divide-y divide-gray-200">
                            <tr
                              v-for="(item, index) in filteredReportData"
                              :key="index"
                            >
                              <td class="px-6 py-4">
                                {{ item.searchKeyword }}
                              </td>
                              <td class="px-6 py-4 whitespace-nowrap">
                                {{
                                  formatImpressions(
                                    item.insightsValue.value ||
                                      item.insightsValue.threshold
                                  )
                                }}
                              </td>
                              <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                  class="px-2 py-1 text-xs font-medium rounded-full"
                                  :class="
                                    getVolumeTagClass(
                                      item.insightsValue.value ||
                                        item.insightsValue.threshold
                                    )
                                  "
                                >
                                  {{
                                    getVolumeLabel(
                                      item.insightsValue.value ||
                                        item.insightsValue.threshold
                                    )
                                  }}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="isGenerating"
                    class="grid grid-cols-1 md:grid-cols-2 gap-4"
                  >
                    <v-skeleton-loader
                      v-for="n in 4"
                      :key="n"
                      type="card"
                      class="bg-white rounded-lg"
                    ></v-skeleton-loader>
                  </div>
                </div>
              </v-tab-item>

              <!-- Recommended Keywords Tab -->
              <v-tab-item>
                <GmbRecommendedKeywords />
              </v-tab-item>
            </v-tabs-items>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "@/utils/firebase.utils";
import connectedAppsMixins from "@/mixins/connected.apps.mixins";
import NoLocationsConnected from "@/components/ui/common/NoLocationsConnected.vue";
import GmbRecommendedKeywords from "./gmb_competitors_components/GmbRecommendedKeywords.vue";
import Chart from "chart.js/auto";

export default {
  name: "GmbSearchReport",
  components: {
    NoLocationsConnected,
    GmbRecommendedKeywords,
  },
  mixins: [connectedAppsMixins],
  data() {
    return {
      activeTab: 0,
      selectedStartMonthYear: null,
      selectedEndMonthYear: null,
      startMonthYearOptions: [],
      endMonthYearOptions: [],
      isGenerating: false,
      isDownloading: false,
      reportData: null,
      locationSearch: "",
      selectedStore: null,
      stores: [],
      selectedWID: null,
      showDropdown: false,
      lastGeneratedAt: null,
      showValidation: false,
      volumeFilter: "all",
      datePickerMenu: false,
      dateRangeDisplay: "",
      showAllKeywords: false,
      loading: true,
      summaryData: null,
      categoryChart: null,
      selectedTimeRange: "3m",
      keywordSearch: "",
      brandKeywords: [],
      selectedCategories: [],
      selectedTrends: [],
      totalTrafficTrendChart: null,
      keywordTrendCharts: {},
      categoryTrendCharts: {},
      sortColumn: "traffic",
      sortDirection: "desc",
      isRefreshingData: false,
      isUpdatingTimeRange: false,
      selectedMonths: [],
      availableMonths: [],
      loadingSummaryCards: false,
      loadingCategoryDistribution: false,
      loadingTopKeywords: false,
      loadingKeywordSummary: false,
    };
  },
  computed: {
    hasData() {
      return this.reportData?.length;
    },
    filteredStores() {
      if (!this.locationSearch) return this.stores;
      const searchTerm = this.locationSearch.toLowerCase();
      return this.stores.filter(
        (store) =>
          store.sub_locality?.toLowerCase().includes(searchTerm) ||
          store.city?.toLowerCase().includes(searchTerm) ||
          store.gmbProfile?.title?.toLowerCase().includes(searchTerm)
      );
    },
    filteredReportData() {
      if (!this.reportData) return [];

      // First filter out long keywords with low impressions
      const filteredByLength = this.reportData.filter((item) => {
        const wordCount = item.searchKeyword.split(" ").length;
        const impressions =
          item.insightsValue.value || item.insightsValue.threshold;
        return wordCount <= 10 || impressions > 15;
      });

      // Then apply volume filter
      if (this.volumeFilter === "all") {
        return filteredByLength;
      }

      return filteredByLength.filter((item) => {
        const impressions =
          item.insightsValue.value || item.insightsValue.threshold;

        if (this.volumeFilter === "high") {
          return impressions > 30;
        } else if (this.volumeFilter === "medium") {
          return impressions >= 15 && impressions <= 30;
        } else if (this.volumeFilter === "low") {
          return impressions < 15;
        }

        return true;
      });
    },
    topKeywords() {
      // Sort by impression count (descending) and take top 10
      return [...this.filteredReportData]
        .sort((a, b) => {
          const aImpressions =
            a.insightsValue.value || a.insightsValue.threshold;
          const bImpressions =
            b.insightsValue.value || b.insightsValue.threshold;
          return bImpressions - aImpressions;
        })
        .slice(0, 10);
    },
    maxImpressionCount() {
      if (!this.filteredReportData.length) return 0;
      return Math.max(
        ...this.filteredReportData.map(
          (item) => item.insightsValue.value || item.insightsValue.threshold
        )
      );
    },
    filteredBrandKeywords() {
      let filtered = this.brandKeywords;

      // Apply category filter
      if (this.selectedCategories.length > 0) {
        filtered = filtered.filter((keyword) =>
          this.selectedCategories.includes(keyword.category)
        );
      }

      // Apply trend filter
      if (this.selectedTrends.length > 0) {
        filtered = filtered.filter((keyword) =>
          this.selectedTrends.includes(this.getTrendCategory(keyword.keyword))
        );
      }

      // Apply search filter
      if (this.keywordSearch) {
        const searchTerm = this.keywordSearch.toLowerCase();
        filtered = filtered.filter(
          (keyword) =>
            keyword.keyword.toLowerCase().includes(searchTerm) ||
            keyword.category.toLowerCase().includes(searchTerm)
        );
      }

      // Apply sorting
      filtered.sort((a, b) => {
        let aValue, bValue;

        if (this.sortColumn === "trafficPercentage") {
          aValue = this.getTrafficPercentageFromTotal(a.traffic);
          bValue = this.getTrafficPercentageFromTotal(b.traffic);
        } else {
          aValue = a[this.sortColumn];
          bValue = b[this.sortColumn];
        }

        if (this.sortDirection === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      return filtered;
    },
    orderedCategoryDistribution() {
      if (!this.summaryData?.categoryDistribution) return [];
      const categoryOrder = [
        "transactional",
        "navigational",
        "branded",
        "informational",
      ];
      return this.summaryData.categoryDistribution.slice().sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.category);
        const bIndex = categoryOrder.indexOf(b.category);
        return aIndex - bIndex;
      });
    },
    orderedCategories() {
      const categoryOrder = [
        "transactional",
        "navigational",
        "branded",
        "informational",
      ];
      if (!this.summaryData?.categoryWiseData) return [];
      return categoryOrder.filter(
        (category) => this.summaryData.categoryWiseData[category]
      );
    },
    filteredOrderedCategories() {
      return this.orderedCategories.filter(
        (category) => this.summaryData?.categoryWiseData[category]
      );
    },
  },
  methods: {
    clearLocation() {
      this.locationSearch = "";
      this.selectedStore = null;
      this.selectedWID = null;
    },
    async initializeData() {
      try {
        this.loading = true;
        // First fetch stores
        await this.fetchStores();
        // Then fetch summary data
        await this.fetchSummaryData(true); // true indicates initial load
        // Initialize available months
        this.initializeAvailableMonths();
      } catch (error) {
        console.error("Error initializing data:", error);
      } finally {
        this.loading = false;
      }
    },

    initializeAvailableMonths() {
      // Generate last 24 months, skipping current month
      const months = [];
      const currentDate = new Date();
      for (let i = 1; i <= 24; i++) {
        // Start from 1 to skip current month
        const date = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() - i,
          1
        );
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const monthText = date.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
        months.push({
          value: `${year}-${month}`,
          text: monthText,
        });
      }
      this.availableMonths = months;
    },

    toggleMonth(monthValue) {
      const index = this.selectedMonths.indexOf(monthValue);
      if (index === -1) {
        this.selectedMonths.push(monthValue);
      } else {
        this.selectedMonths.splice(index, 1);
      }

      // If we have selected months, update the data
      if (this.selectedMonths.length > 0) {
        this.handleCustomTimeRangeChange();
      }
    },

    async handleCustomTimeRangeChange() {
      try {
        this.isUpdatingTimeRange = true;
        await this.fetchSummaryDataForSelectedMonths();
      } finally {
        this.isUpdatingTimeRange = false;
      }
    },

    async fetchSummaryDataForSelectedMonths() {
      try {
        if (this.selectedMonths.length === 0) {
          this.summaryData = null;
          this.brandKeywords = [];
          return;
        }

        // Destroy existing charts before re-rendering
        if (this.categoryChart) {
          this.categoryChart.destroy();
          this.categoryChart = null;
        }
        if (this.totalTrafficTrendChart) {
          this.totalTrafficTrendChart.destroy();
          this.totalTrafficTrendChart = null;
        }
        Object.values(this.categoryTrendCharts).forEach((chart) =>
          chart.destroy()
        );
        this.categoryTrendCharts = {};
        Object.values(this.keywordTrendCharts).forEach((chart) =>
          chart.destroy()
        );
        this.keywordTrendCharts = {};

        // Get data for selected months
        const summaryRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "search_insights_summary"
        );

        const monthlyData = [];
        for (const month of this.selectedMonths) {
          const docRef = doc(summaryRef, month);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            monthlyData.push({
              ...docSnap.data(),
              month: month,
            });
          }
        }

        if (monthlyData.length > 0) {
          this.summaryData = this.aggregateMonthlyData(monthlyData);
          this.processBrandKeywords();

          // Wait for DOM to update and ensure it's ready
          await this.$nextTick();
          await new Promise((resolve) => setTimeout(resolve, 100));

          try {
            // Initialize charts
            this.initializeCategoryChart();
            this.initializeTotalTrafficTrend();

            // Initialize category trend charts
            if (this.summaryData.categoryDistribution) {
              this.orderedCategoryDistribution.forEach((category, index) => {
                this.initializeCategoryTrendChart(
                  this.getCategoryIndex(category.category),
                  category.category
                );
              });
            }

            // Initialize keyword trend charts
            if (this.summaryData.categoryWiseData) {
              this.reinitializeCategoryKeywordCharts();
            }

            // Initialize brand keyword trend charts
            if (this.filteredBrandKeywords.length > 0) {
              this.reinitializeBrandKeywordCharts();
            }
          } catch (chartError) {
            console.error("Error initializing charts:", chartError);
          }
        } else {
          this.summaryData = null;
          this.brandKeywords = [];
        }
      } catch (error) {
        console.error(
          "Error fetching summary data for selected months:",
          error
        );
        this.summaryData = null;
        this.brandKeywords = [];
      }
    },

    generateMonthYearOptions(isEndMonth = false) {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1; // Months are 0-indexed
      const options = [];
      const startMonthYear = this.selectedStartMonthYear
        ? this.selectedStartMonthYear.split("-")
        : null;

      for (let year = currentYear; year > currentYear - 3; year--) {
        for (let month = 1; month <= 12; month++) {
          // Iterate from January to December
          // Logic for disabling options
          if (isEndMonth) {
            if (!startMonthYear) {
              continue; // Skip if start month is not selected
            }
            const selectedStartMonth = parseInt(startMonthYear[0]);
            const selectedStartYear = parseInt(startMonthYear[1]);

            // Disable months before the selected start month
            if (
              year < selectedStartYear ||
              (year === selectedStartYear && month < selectedStartMonth)
            ) {
              continue; // Skip months before the selected start month
            }

            // Ensure end month does not exceed the previous month (skip current month)
            if (year === currentYear && month >= currentMonth) {
              continue; // Skip current month and future months for end month
            }
          } else {
            // For start month, skip current month and future months
            if (year === currentYear && month >= currentMonth) {
              continue; // Skip current month and future months for start month
            }
          }
          options.push({
            text:
              new Date(year, month - 1).toLocaleString("default", {
                month: "long",
              }) + ` ${year}`,
            value: `${month}-${year}`, // Format: month-year
          });
        }
      }

      // Sort options in reverse order by year and month
      options.sort((a, b) => {
        const [monthA, yearA] = a.value.split("-").map(Number);
        const [monthB, yearB] = b.value.split("-").map(Number);
        return yearB - yearA || monthB - monthA; // Sort by year (descending), then by month (descending)
      });

      return options;
    },
    async fetchStores() {
      try {
        const storeRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces"
        );
        const storeQuery = query(
          storeRef,
          where("admin", "==", this.WID),
          where("storeWorkspace", "==", true)
        );

        const querySnapshot = await getDocs(storeQuery);
        this.stores = querySnapshot.docs.map((docSnapshot) => ({
          ...docSnapshot.data(),
          wid: docSnapshot.id,
        }));
      } catch (error) {
        console.error("Error fetching stores:", error);
        this.$snackbar.show("Failed to load locations. Please try again.");
      }
    },
    toggleStoreDropdown(event) {
      if (event) {
        event.stopPropagation();
      }
      this.showDropdown = !this.showDropdown;
    },
    handleSearchInput() {
      this.showDropdown = true;
    },
    handleClickOutside(event) {
      const profileDropdown = this.$refs.showStoreDropdown;
      if (profileDropdown && !profileDropdown.contains(event.target)) {
        this.closeProfileDropdown();
      }
    },
    closeProfileDropdown() {
      if (this.showDropdown) {
        this.showDropdown = false;
      }
    },
    selectStore(store) {
      this.selectedStore = store;
      this.showDropdown = false;
      this.selectedWID = store.wid;
      this.locationSearch = store.sub_locality || store.gmbProfile?.title || "";
    },
    applyPresetRange(preset) {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1; // Months are 0-indexed

      let startMonth, startYear, endMonth, endYear;

      if (preset === "last3Months") {
        // Calculate 3 months back, skipping current month
        if (currentMonth > 4) {
          // Need more months since we skip current
          startMonth = currentMonth - 4;
          startYear = currentYear;
        } else {
          startMonth = 12 + (currentMonth - 4); // Wrap around to previous year
          startYear = currentYear - 1;
        }
        endMonth = currentMonth - 1; // End at previous month
        endYear = currentYear;
      } else if (preset === "last6Months") {
        // Calculate 6 months back, skipping current month
        if (currentMonth > 7) {
          // Need more months since we skip current
          startMonth = currentMonth - 7;
          startYear = currentYear;
        } else {
          startMonth = 12 + (currentMonth - 7); // Wrap around to previous year
          startYear = currentYear - 1;
        }
        endMonth = currentMonth - 1; // End at previous month
        endYear = currentYear;
      }

      this.selectedStartMonthYear = `${startMonth}-${startYear}`;
      this.selectedEndMonthYear = `${endMonth}-${endYear}`;
      this.updateDateRangeDisplay();
    },
    updateDateRangeDisplay() {
      if (this.selectedStartMonthYear && this.selectedEndMonthYear) {
        const [startMonth, startYear] = this.selectedStartMonthYear.split("-");
        const [endMonth, endYear] = this.selectedEndMonthYear.split("-");

        const startDate = new Date(startYear, startMonth - 1);
        const endDate = new Date(endYear, endMonth - 1);

        const startStr = startDate.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
        const endStr = endDate.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });

        this.dateRangeDisplay = `${startStr} - ${endStr}`;
      } else {
        this.dateRangeDisplay = "Select Date Range";
      }
    },
    applyDateRange() {
      this.updateDateRangeDisplay();
      this.datePickerMenu = false;
    },
    async generateReport() {
      this.showValidation = true;

      if (
        !this.selectedStore ||
        !this.selectedStartMonthYear ||
        !this.selectedEndMonthYear
      ) {
        return;
      }

      this.showValidation = false;
      this.isGenerating = true;
      this.$snackbar.show("Generating search report...");

      try {
        const localStartMonth = JSON.stringify(this.selectedStartMonthYear);
        const localEndMonth = JSON.stringify(this.selectedEndMonthYear);
        sessionStorage.setItem(
          "gmbsearchReportStartMonthYear",
          localStartMonth
        );
        sessionStorage.setItem("gmbsearchReportEndMonthYear", localEndMonth);
        sessionStorage.setItem(
          "gmbsearchReportSelectedStore",
          JSON.stringify(this.selectedStore)
        );

        const [startMonth, startYear] = this.selectedStartMonthYear.split("-");
        const [endMonth, endYear] = this.selectedEndMonthYear.split("-");
        const payload = {
          uid: this.currentUID,
          wid: this.selectedStore.wid,
          locationId: this.selectedStore.gmbProfile?.name?.replace(
            "locations/",
            ""
          ),
          startYear: startYear,
          endYear: endYear,
          startMonth: startMonth,
          endMonth: endMonth,
        };
        const idToken = await auth.currentUser.getIdToken();
        let result;
        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/gmbSearchReportFunction`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ data: payload }),
          }
        );
        if (response.ok) {
          const resultData = await response.json();
          result = { data: resultData };
          // console.log("Success! Data from server fetchPreviousMetrics:", result);
          this.reportData = result?.data?.searchKeywordsCounts || [];
          if (this.reportData?.length > 0) {
            sessionStorage.setItem(
              "gmbsearchReportData",
              JSON.stringify(this.reportData)
            );

            const timestamp = new Date().toISOString();
            sessionStorage.setItem("gmbsearchReportLastGeneratedAt", timestamp);
            this.lastGeneratedAt = this.formatTimeAgo(timestamp);
            // console.log(this.reportData, "this is report data");
            this.$snackbar.show("Report generated successfully!");
          }
        }
      } catch (error) {
        console.error("Error generating report:", error);
        this.$snackbar.show("Failed to generate report. Please try again.");
      } finally {
        this.isGenerating = false;
        this.$snackbar.hide();
      }
    },
    formatImpressions(value) {
      if (value <= 15) {
        return `${value} (approx.)`;
      }
      return value; // Return the value as is if greater than 15
    },
    formatTimeAgo(timestamp) {
      const now = new Date();
      const past = new Date(timestamp);
      const diffInSeconds = Math.floor((now - past) / 1000);

      const intervals = [
        { label: "year", seconds: 31536000 },
        { label: "month", seconds: 2592000 },
        { label: "day", seconds: 86400 },
        { label: "hour", seconds: 3600 },
        { label: "minute", seconds: 60 },
        { label: "second", seconds: 1 },
      ];

      for (const interval of intervals) {
        const count = Math.floor(diffInSeconds / interval.seconds);
        if (count > 0) {
          return `${count} ${interval.label}${count !== 1 ? "s" : ""} ago`;
        }
      }
      return "just now";
    },
    truncateText(text, maxLength) {
      if (!text) return "";
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;
    },
    handleConnectGMB() {
      try {
        this.$snackbar.show("Please wait a moment");
        this.isLoading = true;

        // Use the initConnection method from the connected.apps.mixins.js
        this.initConnection("google_business_profile", "bulk");

        setTimeout(() => {
          this.$snackbar.hide();
        }, 2000);
      } finally {
        this.isLoading = false;
      }
    },
    getVolumeLabel(impressions) {
      if (impressions > 30) return "High";
      if (impressions >= 15) return "Medium";
      return "Low";
    },

    getVolumeTagClass(impressions) {
      if (impressions > 30) return "bg-green-100 text-green-800";
      if (impressions >= 15) return "bg-blue-100 text-blue-800";
      return "bg-gray-100 text-gray-800";
    },

    getVolumeClass(impressions) {
      if (impressions > 30) return "high-volume";
      if (impressions >= 15) return "medium-volume";
      return "low-volume";
    },

    getBarColorClass(impressions) {
      if (impressions > 30) return "bg-green-500";
      if (impressions >= 15) return "bg-blue-500";
      return "bg-gray-500";
    },

    getBarWidthPercentage(impressions) {
      if (this.maxImpressionCount === 0) return 0;
      return (impressions / this.maxImpressionCount) * 100;
    },

    downloadCSV() {
      if (!this.reportData || this.reportData.length === 0) {
        this.$snackbar.show("No data to download");
        return;
      }

      try {
        this.$snackbar.show("Preparing CSV download...");

        // Create CSV content
        let csvContent = "Keyword,Impressions,Volume Category\n";

        this.filteredReportData.forEach((item) => {
          const keyword = item.searchKeyword.replace(/,/g, " "); // Replace commas to avoid CSV parsing issues
          const impressions = this.formatImpressions(
            item.insightsValue.value || item.insightsValue.threshold
          );
          const volumeCategory = this.getVolumeLabel(
            item.insightsValue.value || item.insightsValue.threshold
          );
          csvContent += `"${keyword}",${impressions},"${volumeCategory}"\n`;
        });

        // Create and trigger download
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        // Generate filename with location and date range
        const locationName = this.selectedStore?.sub_locality || "location";
        const dateStr = new Date().toISOString().split("T")[0];

        link.href = url;
        link.setAttribute(
          "download",
          `search-insights-${locationName}-${dateStr}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$snackbar.show("CSV downloaded successfully");
      } catch (error) {
        console.error("Error downloading CSV:", error);
        this.$snackbar.show("Failed to download CSV");
      } finally {
        this.$snackbar.hide();
      }
    },
    async fetchSummaryData(isInitialLoad = false) {
      try {
        // Only set isRefreshingData if it's not the initial load and not updating time range
        if (!isInitialLoad && !this.isUpdatingTimeRange) {
          this.isRefreshingData = true;
        }

        const currentDate = new Date();
        const monthsToFetch =
          this.selectedTimeRange === "3m"
            ? 3
            : this.selectedTimeRange === "6m"
            ? 6
            : 12;

        // Generate months array based on selected range, skipping current month
        const months = [];
        for (let i = 1; i <= monthsToFetch; i++) {
          // Start from 1 to skip current month
          const date = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() - i,
            1
          );
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          months.push(`${year}-${month}`);
        }

        // Get data for all months in range
        const summaryRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "search_insights_summary"
        );

        // Fetch each month's data individually since they are separate documents
        const monthlyData = [];
        for (const month of months) {
          const docRef = doc(summaryRef, month);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            monthlyData.push({
              ...docSnap.data(),
              month: month,
            });
          }
        }

        // Destroy existing charts before re-rendering
        if (this.categoryChart) {
          this.categoryChart.destroy();
          this.categoryChart = null;
        }
        if (this.totalTrafficTrendChart) {
          this.totalTrafficTrendChart.destroy();
          this.totalTrafficTrendChart = null;
        }
        Object.values(this.categoryTrendCharts).forEach((chart) =>
          chart.destroy()
        );
        this.categoryTrendCharts = {};
        Object.values(this.keywordTrendCharts).forEach((chart) =>
          chart.destroy()
        );
        this.keywordTrendCharts = {};

        if (monthlyData.length > 0) {
          this.summaryData = this.aggregateMonthlyData(monthlyData);
          this.processBrandKeywords();

          // Wait for DOM to update and ensure it's ready
          await this.$nextTick();
          await new Promise((resolve) => setTimeout(resolve, 100)); // Small delay to ensure DOM is ready

          try {
            // Initialize charts
            this.initializeCategoryChart();
            this.initializeTotalTrafficTrend();

            // Initialize category trend charts
            if (this.summaryData.categoryDistribution) {
              this.orderedCategoryDistribution.forEach((category, index) => {
                this.initializeCategoryTrendChart(
                  this.getCategoryIndex(category.category),
                  category.category
                );
              });
            }

            // Initialize keyword trend charts for Top Keywords by Category
            if (this.summaryData.categoryWiseData) {
              this.reinitializeCategoryKeywordCharts();
            }

            // Initialize brand keyword trend charts
            if (this.filteredBrandKeywords.length > 0) {
              this.reinitializeBrandKeywordCharts();
            }
          } catch (chartError) {
            console.error("Error initializing charts:", chartError);
          }
        } else {
          this.summaryData = null;
          this.brandKeywords = [];
        }
      } catch (error) {
        console.error("Error fetching summary data:", error);
        this.summaryData = null;
        this.brandKeywords = [];
      } finally {
        if (!isInitialLoad && !this.isUpdatingTimeRange) {
          this.isRefreshingData = false;
        }
      }
    },

    aggregateMonthlyData(monthlyData) {
      // Aggregate data from multiple months
      const aggregated = {
        totalBrandTraffic: 0,
        storeCount: 0,
        categoryDistribution: [],
        categoryWiseData: {},
        storeWiseSummary: [],
        lastUpdated: monthlyData[0]?.lastUpdated,
        month: monthlyData[0]?.month,
      };

      // Initialize category distribution
      const categoryMap = new Map();
      const storeMap = new Map();
      const keywordMap = new Map();

      // Process each month's data
      monthlyData.forEach((data) => {
        // Aggregate total traffic
        aggregated.totalBrandTraffic += data.totalBrandTraffic || 0;
        aggregated.storeCount = Math.max(
          aggregated.storeCount,
          data.storeCount || 0
        );

        // Process category distribution
        if (data.categoryDistribution) {
          data.categoryDistribution.forEach((cat) => {
            if (!categoryMap.has(cat.category)) {
              categoryMap.set(cat.category, {
                category: cat.category,
                totalTraffic: 0,
                keywordCount: 0,
                percentage: 0,
              });
            }
            const existingCat = categoryMap.get(cat.category);
            existingCat.totalTraffic += cat.totalTraffic || 0;
            existingCat.keywordCount += cat.keywordCount || 0;
            existingCat.percentage += cat.percentage || 0;
          });
        }

        // Process category-wise data
        if (data.categoryWiseData) {
          Object.entries(data.categoryWiseData).forEach(
            ([category, catData]) => {
              if (!aggregated.categoryWiseData[category]) {
                aggregated.categoryWiseData[category] = {
                  keywords: [],
                  totalTraffic: 0,
                };
              }

              if (catData.keywords) {
                catData.keywords.forEach((keyword) => {
                  const key = `${category}-${keyword.keyword}`;
                  if (!keywordMap.has(key)) {
                    keywordMap.set(key, {
                      keyword: keyword.keyword,
                      category: category,
                      traffic: 0,
                      trafficPercentage: 0,
                      activeStores: 0,
                    });
                  }
                  const existingKeyword = keywordMap.get(key);
                  existingKeyword.traffic += keyword.traffic || 0;
                  existingKeyword.trafficPercentage +=
                    keyword.trafficPercentage || 0;
                  existingKeyword.activeStores++;
                });
              }
            }
          );
        }

        // Process store-wise summary
        if (data.storeWiseSummary) {
          data.storeWiseSummary.forEach((store) => {
            if (!storeMap.has(store.storeId)) {
              storeMap.set(store.storeId, {
                storeId: store.storeId,
                totalTraffic: 0,
                topKeywords: [],
              });
            }
            const existingStore = storeMap.get(store.storeId);
            existingStore.totalTraffic += store.totalTraffic || 0;

            if (store.topKeywords) {
              store.topKeywords.forEach((keyword) => {
                const existingKeyword = existingStore.topKeywords.find(
                  (k) => k.keyword === keyword.keyword
                );
                if (existingKeyword) {
                  existingKeyword.traffic += keyword.traffic || 0;
                } else {
                  existingStore.topKeywords.push({ ...keyword });
                }
              });
            }
          });
        }
      });

      // Convert maps to arrays and sort
      aggregated.categoryDistribution = Array.from(categoryMap.values()).sort(
        (a, b) => b.totalTraffic - a.totalTraffic
      );

      // Normalize percentages to ensure they sum to 100
      const totalPercentage = aggregated.categoryDistribution.reduce(
        (sum, cat) => sum + cat.percentage,
        0
      );
      if (totalPercentage > 0) {
        aggregated.categoryDistribution = aggregated.categoryDistribution.map(
          (cat) => ({
            ...cat,
            percentage: (cat.percentage / totalPercentage) * 100,
          })
        );
      }

      // Process category-wise data
      Object.keys(aggregated.categoryWiseData).forEach((category) => {
        const categoryKeywords = Array.from(keywordMap.values())
          .filter((k) => k.category === category)
          .sort((a, b) => b.trafficPercentage - a.trafficPercentage);
        aggregated.categoryWiseData[category].keywords = categoryKeywords;
      });

      // Process store-wise summary
      aggregated.storeWiseSummary = Array.from(storeMap.values())
        .map((store) => ({
          ...store,
          topKeywords: store.topKeywords
            .sort((a, b) => b.traffic - a.traffic)
            .slice(0, 5),
        }))
        .sort((a, b) => b.totalTraffic - a.totalTraffic);

      // Add monthly data to the aggregated result
      aggregated.monthlyData = {};
      monthlyData.forEach((data) => {
        const month = data.month;
        aggregated.monthlyData[month] = {};

        // Process category-wise monthly data
        if (data.categoryWiseData) {
          Object.entries(data.categoryWiseData).forEach(
            ([category, catData]) => {
              if (!aggregated.monthlyData[month][category]) {
                aggregated.monthlyData[month][category] = {
                  totalTraffic: 0,
                  keywords: {},
                };
              }

              if (catData.keywords) {
                catData.keywords.forEach((keyword) => {
                  if (
                    !aggregated.monthlyData[month][category].keywords[
                      keyword.keyword
                    ]
                  ) {
                    aggregated.monthlyData[month][category].keywords[
                      keyword.keyword
                    ] = {
                      traffic: 0,
                      trafficPercentage: 0,
                    };
                  }
                  aggregated.monthlyData[month][category].keywords[
                    keyword.keyword
                  ].traffic += keyword.traffic || 0;
                  aggregated.monthlyData[month][category].keywords[
                    keyword.keyword
                  ].trafficPercentage += keyword.trafficPercentage || 0;
                });
              }

              aggregated.monthlyData[month][category].totalTraffic +=
                catData.totalTraffic || 0;
            }
          );
        }
      });

      return aggregated;
    },

    processBrandKeywords() {
      if (!this.summaryData?.categoryWiseData) return;

      const keywordMap = new Map();

      // Process keywords from all categories
      Object.entries(this.summaryData.categoryWiseData).forEach(
        ([category, data]) => {
          if (data.keywords) {
            data.keywords.forEach((keyword) => {
              // Skip keywords longer than 8 words
              if (keyword.keyword.split(" ").length > 8) return;

              const existing = keywordMap.get(keyword.keyword);
              if (existing) {
                existing.traffic += keyword.traffic;
                existing.activeStores++;
              } else {
                keywordMap.set(keyword.keyword, {
                  keyword: keyword.keyword,
                  category: category,
                  traffic: keyword.traffic,
                  activeStores: 1,
                });
              }
            });
          }
        }
      );

      // Convert to array and sort by traffic (not percentage since we calculate it dynamically)
      this.brandKeywords = Array.from(keywordMap.values()).sort(
        (a, b) => b.traffic - a.traffic
      );
    },

    async handleTimeRangeChange() {
      try {
        this.isUpdatingTimeRange = true;

        // If custom is selected, don't fetch data until months are selected
        if (this.selectedTimeRange === "custom") {
          // Clear current data and wait for user to select months
          this.selectedMonths = [];
          return;
        }

        // For regular time ranges (3m, 6m, 12m), fetch data normally
        await this.fetchSummaryData(false); // false indicates this is a refresh operation
      } finally {
        this.isUpdatingTimeRange = false;
      }
    },

    initializeCategoryChart() {
      if (!this.summaryData?.categoryDistribution) return;

      const ctx = this.$refs.categoryChart?.getContext("2d");
      if (!ctx) {
        console.warn("Category chart canvas not ready");
        return;
      }

      if (this.categoryChart) {
        this.categoryChart.destroy();
      }

      try {
        const data = this.orderedCategoryDistribution;
        this.categoryChart = new Chart(ctx, {
          type: "doughnut",
          data: {
            labels: data.map(
              (d) => d.category.charAt(0).toUpperCase() + d.category.slice(1)
            ),
            datasets: [
              {
                data: data.map((d) => d.totalTraffic),
                backgroundColor: data.map((d) =>
                  this.getCategoryColor(d.category)
                ),
                borderWidth: 0,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: "bottom",
                labels: {
                  padding: 20,
                  usePointStyle: true,
                },
              },
              tooltip: {
                callbacks: {
                  label: function (context) {
                    const label = context.label || "";
                    const value = context.raw || 0;
                    const percentage =
                      data[context.dataIndex].percentage.toFixed(1);
                    return `${label}: ${value.toLocaleString()} (${percentage}%)`;
                  },
                },
              },
            },
          },
        });
      } catch (error) {
        console.error("Error creating category chart:", error);
      }
    },

    getCategoryColor(category) {
      const colors = {
        branded: "#4CAF50",
        navigational: "#2196F3",
        transactional: "#FF9800",
        informational: "#9C27B0",
      };
      return colors[category] || "#757575";
    },

    getCategoryIcon(category) {
      const icons = {
        branded: "mdi-tag",
        navigational: "mdi-map-marker",
        transactional: "mdi-cart",
        informational: "mdi-information",
      };
      return icons[category] || "mdi-help-circle";
    },

    getCategoryColorClass(category) {
      const colors = {
        branded: "bg-green-100 text-green-800",
        navigational: "bg-blue-100 text-blue-800",
        transactional: "bg-orange-100 text-orange-800",
        informational: "bg-purple-100 text-purple-800",
      };
      return colors[category] || "bg-gray-100 text-gray-800";
    },

    formatNumber(num) {
      return new Intl.NumberFormat().format(num);
    },

    formatLastUpdated(timestamp) {
      if (!timestamp) return "N/A";
      const date = timestamp.toDate();
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(date);
    },

    getTotalKeywords() {
      if (!this.summaryData?.categoryDistribution) return 0;
      return this.summaryData.categoryDistribution.reduce(
        (sum, cat) => sum + cat.keywordCount,
        0
      );
    },

    getStoreName(storeId) {
      const store = this.stores.find((s) => s.wid === storeId);
      return store?.gmbProfile?.title || store?.sub_locality || "Unknown Store";
    },

    getTrafficPercentageClass(percentage) {
      if (percentage >= 10) return "bg-green-500";
      if (percentage >= 5) return "bg-blue-500";
      if (percentage >= 2) return "bg-yellow-500";
      return "bg-gray-500";
    },

    toggleCategory(category) {
      const index = this.selectedCategories.indexOf(category);
      if (index === -1) {
        this.selectedCategories.push(category);
      } else {
        this.selectedCategories.splice(index, 1);
      }
    },

    toggleTrend(trend) {
      const index = this.selectedTrends.indexOf(trend);
      if (index === -1) {
        this.selectedTrends.push(trend);
      } else {
        this.selectedTrends.splice(index, 1);
      }
    },

    getTrendIcon(trend) {
      const icons = {
        Rising: "mdi-trending-up",
        Falling: "mdi-trending-down",
        Holding: "mdi-trending-neutral",
      };
      return icons[trend] || "mdi-trending-neutral";
    },

    getCategoryPercentage(category) {
      if (!this.summaryData?.categoryDistribution) return 0;
      const categoryData = this.summaryData.categoryDistribution.find(
        (cat) => cat.category === category
      );
      return categoryData?.percentage || 0;
    },

    getCategoryTextColor(category) {
      const colors = {
        branded: "text-green-700",
        navigational: "text-blue-700",
        transactional: "text-orange-700",
        informational: "text-purple-700",
      };
      return colors[category] || "text-gray-700";
    },

    initializeCategoryTrendChart(index, category) {
      const canvas = this.$refs[`categoryTrend_${index}`]?.[0];
      if (!canvas) {
        console.warn(`Category trend canvas ${index} not ready`);
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.warn(`Category trend canvas context ${index} not ready`);
        return;
      }

      const monthlyData = this.getCategoryMonthlyDataWithDifference(category);
      const months = Object.keys(monthlyData).sort();
      const data = months.map((month) => monthlyData[month].traffic);

      const chartId = `categoryTrend_${index}`;
      if (this.categoryTrendCharts[chartId]) {
        this.categoryTrendCharts[chartId].destroy();
      }

      try {
        this.categoryTrendCharts[chartId] = new Chart(ctx, {
          type: "line",
          data: {
            labels: months,
            datasets: [
              {
                data,
                borderColor: this.getCategoryColor(category),
                backgroundColor: this.getCategoryColor(category) + "20",
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: false,
              },
            },
            scales: {
              x: {
                display: false,
              },
              y: {
                display: false,
              },
            },
            animation: false,
          },
        });
      } catch (error) {
        console.error(`Error creating category trend chart ${index}:`, error);
      }
    },

    initializeKeywordTrendChart(chartId, keyword, monthlyData) {
      if (!monthlyData) return;

      const canvas = this.$refs[chartId]?.[0];
      if (!canvas) {
        console.warn(`Keyword trend canvas ${chartId} not ready`);
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.warn(`Keyword trend canvas context ${chartId} not ready`);
        return;
      }

      // Get monthly data for this keyword
      const keywordData = this.getKeywordMonthlyData(keyword);
      const months = Object.keys(keywordData).sort();
      const data = months.map((month) => keywordData[month].traffic);

      // Calculate trend color based on data
      const firstValue = data[0] || 0;
      const lastValue = data[data.length - 1] || 0;
      const trendColor =
        lastValue > firstValue
          ? "#10B981"
          : lastValue < firstValue
          ? "#EF4444"
          : "#6B7280";

      // Clean up existing chart if it exists
      if (this.keywordTrendCharts[chartId]) {
        this.keywordTrendCharts[chartId].destroy();
        delete this.keywordTrendCharts[chartId];
      }

      try {
        // Create new chart
        this.keywordTrendCharts[chartId] = new Chart(ctx, {
          type: "line",
          data: {
            labels: months,
            datasets: [
              {
                data,
                borderColor: trendColor,
                backgroundColor: trendColor + "20",
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: false,
              },
            },
            scales: {
              x: {
                display: false,
              },
              y: {
                display: false,
              },
            },
            animation: false,
          },
        });
      } catch (error) {
        console.error(`Error creating keyword trend chart ${chartId}:`, error);
      }
    },

    getTrafficTrendColor(data) {
      if (data.length < 2) return "#9CA3AF";

      const firstValue = data[0];
      const lastValue = data[data.length - 1];
      const trend = lastValue - firstValue;

      if (trend > 0) return "#10B981"; // Green for growth
      if (trend < 0) return "#EF4444"; // Red for decline
      return "#9CA3AF"; // Gray for stable
    },

    getKeywordMonthlyData(keyword) {
      if (!this.summaryData?.monthlyData) return {};

      const monthlyData = {};
      Object.entries(this.summaryData.monthlyData).forEach(([month, data]) => {
        let found = false;
        Object.values(data).forEach((category) => {
          if (category.keywords && Array.isArray(category.keywords)) {
            const keywordData = category.keywords.find(
              (k) => k.keyword === keyword
            );
            if (keywordData) {
              monthlyData[month] = {
                traffic: keywordData.traffic,
                percentage: keywordData.trafficPercentage,
              };
              found = true;
            }
          } else if (
            category.keywords &&
            typeof category.keywords === "object"
          ) {
            // Handle case where keywords is an object instead of array
            const keywordData = category.keywords[keyword];
            if (keywordData) {
              monthlyData[month] = {
                traffic: keywordData.traffic,
                percentage: keywordData.trafficPercentage,
              };
              found = true;
            }
          }
        });
        if (!found) {
          monthlyData[month] = { traffic: 0, percentage: 0 };
        }
      });

      return monthlyData;
    },

    formatMonth(monthStr) {
      const [year, month] = monthStr.split("-");
      return new Date(year, month - 1).toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
    },

    getTotalTrafficMonthlyData() {
      if (!this.summaryData?.monthlyData) return {};

      const monthlyData = {};
      const months = Object.keys(this.summaryData.monthlyData).sort();

      months.forEach((month, index) => {
        const traffic = Object.values(
          this.summaryData.monthlyData[month]
        ).reduce((sum, category) => sum + (category.totalTraffic || 0), 0);

        let difference = null;
        if (index > 0) {
          const prevMonth = months[index - 1];
          const prevTraffic = Object.values(
            this.summaryData.monthlyData[prevMonth]
          ).reduce((sum, category) => sum + (category.totalTraffic || 0), 0);

          if (prevTraffic > 0) {
            difference = Math.round(
              ((traffic - prevTraffic) / prevTraffic) * 100
            );
          }
        }

        monthlyData[month] = { traffic, difference };
      });

      return monthlyData;
    },

    getCategoryMonthlyData(category) {
      if (!this.summaryData?.monthlyData) return {};

      const monthlyData = {};
      Object.entries(this.summaryData.monthlyData).forEach(([month, data]) => {
        monthlyData[month] = data[category]?.totalTraffic || 0;
      });

      return monthlyData;
    },

    initializeTotalTrafficTrend() {
      const ctx = this.$refs.totalTrafficTrend?.getContext("2d");
      if (!ctx) {
        console.warn("Total traffic trend canvas not ready");
        return;
      }

      const monthlyData = this.getTotalTrafficMonthlyData();
      const months = Object.keys(monthlyData).sort();
      const data = months.map((month) => monthlyData[month].traffic);

      if (this.totalTrafficTrendChart) {
        this.totalTrafficTrendChart.destroy();
      }

      try {
        this.totalTrafficTrendChart = new Chart(ctx, {
          type: "line",
          data: {
            labels: months,
            datasets: [
              {
                data,
                borderColor: "#4F46E5",
                backgroundColor: "#4F46E520",
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: false,
              },
            },
            scales: {
              x: {
                display: false,
              },
              y: {
                display: false,
              },
            },
            animation: false,
          },
        });
      } catch (error) {
        console.error("Error creating total traffic trend chart:", error);
      }
    },
    async downloadSnapshot() {
      try {
        this.isDownloading = true;
        this.$snackbar.show("Preparing snapshot...");

        // Wait for next render cycle to ensure all charts are rendered
        await this.$nextTick();

        // Additional wait to ensure charts are fully rendered
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Use html2canvas to capture the dashboard content
        const element = document.querySelector(
          ".bg-white.rounded-lg.shadow-sm.p-4.md\\:p-6.mb-6"
        );
        if (!element) {
          throw new Error("Dashboard element not found");
        }

        // Ensure all canvas elements have proper dimensions before capturing
        const canvases = element.querySelectorAll("canvas");
        canvases.forEach((canvas) => {
          if (canvas.width === 0 || canvas.height === 0) {
            // Set minimum dimensions for empty canvases
            canvas.width = 20;
            canvas.height = 8;
            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.fillStyle = "#f3f4f6";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
          }
        });

        // Try with html2canvas first
        try {
          const canvas = await this.$html2canvas(element, {
            scale: 2, // Higher quality
            useCORS: true, // Handle cross-origin images
            logging: false,
            allowTaint: true, // Allow tainted canvas
            foreignObjectRendering: false, // Disable foreign object rendering
            removeContainer: true, // Remove temporary container
            ignoreElements: (element) => {
              // Ignore elements that might cause issues
              return (
                element.tagName === "CANVAS" &&
                (element.width === 0 || element.height === 0)
              );
            },
          });

          await this.downloadCanvasAsImage(canvas);
        } catch (html2canvasError) {
          console.warn(
            "html2canvas failed, trying fallback method:",
            html2canvasError
          );
          // Fallback: try to capture just the visible content without charts
          await this.downloadFallbackSnapshot(element);
        }
      } catch (error) {
        console.error("Error downloading snapshot:", error);
        this.$snackbar.show("Failed to download snapshot. Please try again.");
      } finally {
        this.isDownloading = false;
        this.$snackbar.hide();
      }
    },

    async downloadCanvasAsImage(canvas) {
      // Convert to blob
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, "image/png");
      });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().split("T")[0];
      const location = this.selectedStore?.sub_locality || "all-locations";

      link.href = url;
      link.download = `search-insights-${location}-${timestamp}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.$snackbar.show("Dashboard snapshot downloaded successfully");
    },

    async downloadFallbackSnapshot(element) {
      // Create a simplified version without problematic canvas elements
      const clone = element.cloneNode(true);

      // Remove all canvas elements from the clone
      const canvases = clone.querySelectorAll("canvas");
      canvases.forEach((canvas) => {
        const placeholder = document.createElement("div");
        placeholder.style.width = "20px";
        placeholder.style.height = "8px";
        placeholder.style.backgroundColor = "#f3f4f6";
        placeholder.style.borderRadius = "2px";
        placeholder.style.display = "inline-block";
        canvas.parentNode.replaceChild(placeholder, canvas);
      });

      // Create a temporary container
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "-9999px";
      container.appendChild(clone);
      document.body.appendChild(container);

      try {
        const canvas = await this.$html2canvas(clone, {
          scale: 1,
          useCORS: true,
          logging: false,
          allowTaint: true,
        });

        await this.downloadCanvasAsImage(canvas);
      } finally {
        document.body.removeChild(container);
      }
    },
    sortBrandKeywords(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortColumn = column;
        this.sortDirection = "desc";
      }
    },

    reinitializeBrandKeywordCharts() {
      if (!this.summaryData?.monthlyData) return;

      // Clean up existing brand keyword charts
      Object.keys(this.keywordTrendCharts).forEach((chartId) => {
        if (chartId.startsWith("brandKeywordTrend_")) {
          if (this.keywordTrendCharts[chartId]) {
            this.keywordTrendCharts[chartId].destroy();
            delete this.keywordTrendCharts[chartId];
          }
        }
      });

      // Wait for DOM to be ready and then initialize new charts
      this.$nextTick(() => {
        this.filteredBrandKeywords.forEach((keyword, index) => {
          const chartId = `brandKeywordTrend_${index}`;
          this.initializeKeywordTrendChart(
            chartId,
            keyword.keyword,
            this.summaryData.monthlyData
          );
        });
      });
    },

    reinitializeCategoryKeywordCharts() {
      if (!this.summaryData?.monthlyData || !this.summaryData?.categoryWiseData)
        return;

      // Clean up existing category keyword charts
      Object.keys(this.keywordTrendCharts).forEach((chartId) => {
        if (
          chartId.includes("keywordTrend_") &&
          !chartId.startsWith("brandKeywordTrend_")
        ) {
          if (this.keywordTrendCharts[chartId]) {
            this.keywordTrendCharts[chartId].destroy();
            delete this.keywordTrendCharts[chartId];
          }
        }
      });

      // Wait for DOM to be ready and then initialize new charts
      this.$nextTick(() => {
        this.orderedCategories.forEach((category) => {
          if (this.summaryData.categoryWiseData[category]?.keywords) {
            this.summaryData.categoryWiseData[category].keywords.forEach(
              (keyword, index) => {
                const chartId = `keywordTrend_${category}_${index}`;
                this.initializeKeywordTrendChart(
                  chartId,
                  keyword.keyword,
                  this.summaryData.monthlyData
                );
              }
            );
          }
        });
      });
    },

    downloadKeywordSummary() {
      if (!this.filteredBrandKeywords.length) {
        this.$snackbar.show("No data to download");
        return;
      }

      try {
        this.$snackbar.show("Preparing CSV download...");

        // Create CSV content
        let csvContent =
          "Keyword,Category,Trend,Traffic,Traffic %,Active Stores\n";

        this.filteredBrandKeywords.forEach((keyword) => {
          const keywordText = keyword.keyword.replace(/,/g, " "); // Replace commas to avoid CSV parsing issues
          const category = keyword.category;
          const trend = this.getTrendCategory(keyword.keyword);
          const traffic = keyword.traffic;
          const trafficPercentage = this.getTrafficPercentageFromTotal(
            keyword.traffic
          ).toFixed(1);
          const activeStores = keyword.activeStores;

          csvContent += `"${keywordText}","${category}","${trend}",${traffic},${trafficPercentage}%,${activeStores}\n`;
        });

        // Create and trigger download
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        // Generate filename with time range and date
        const timeRange = this.selectedTimeRange || "12m";
        const dateStr = new Date().toISOString().split("T")[0];

        link.href = url;
        link.setAttribute("download", `traffic-${timeRange}-${dateStr}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.$snackbar.show("CSV downloaded successfully");
      } catch (error) {
        console.error("Error downloading CSV:", error);
        this.$snackbar.show("Failed to download CSV");
      } finally {
        this.$snackbar.hide();
      }
    },
    getTrafficPercentageFromTotal(traffic) {
      if (
        !this.summaryData?.totalBrandTraffic ||
        this.summaryData.totalBrandTraffic === 0
      )
        return 0;
      return (traffic / this.summaryData.totalBrandTraffic) * 100;
    },

    getCategoryIndex(category) {
      const categoryOrder = [
        "transactional",
        "navigational",
        "branded",
        "informational",
      ];
      return categoryOrder.indexOf(category);
    },

    getCategoryMonthlyDataWithDifference(category) {
      if (!this.summaryData?.monthlyData) return {};

      const monthlyData = {};
      const months = Object.keys(this.summaryData.monthlyData).sort();

      months.forEach((month, index) => {
        const data = this.summaryData.monthlyData[month];
        const traffic = data[category]?.totalTraffic || 0;

        let difference = null;
        if (index > 0) {
          const prevMonth = months[index - 1];
          const prevData = this.summaryData.monthlyData[prevMonth];
          const prevTraffic = prevData[category]?.totalTraffic || 0;

          if (prevTraffic > 0) {
            difference = Math.round(
              ((traffic - prevTraffic) / prevTraffic) * 100
            );
          }
        }

        monthlyData[month] = { traffic, difference };
      });

      return monthlyData;
    },

    getKeywordMonthlyDataWithDifference(keyword) {
      if (!this.summaryData?.monthlyData) return {};

      const monthlyData = {};
      const months = Object.keys(this.summaryData.monthlyData).sort();

      months.forEach((month, index) => {
        let traffic = 0;

        // Find the keyword in any category for this month
        Object.values(this.summaryData.monthlyData[month]).forEach(
          (category) => {
            if (category.keywords && typeof category.keywords === "object") {
              if (category.keywords[keyword]) {
                traffic += category.keywords[keyword].traffic || 0;
              }
            }
          }
        );

        let difference = null;
        if (index > 0) {
          const prevMonth = months[index - 1];
          let prevTraffic = 0;

          Object.values(this.summaryData.monthlyData[prevMonth]).forEach(
            (category) => {
              if (category.keywords && typeof category.keywords === "object") {
                if (category.keywords[keyword]) {
                  prevTraffic += category.keywords[keyword].traffic || 0;
                }
              }
            }
          );

          if (prevTraffic > 0) {
            difference = Math.round(
              ((traffic - prevTraffic) / prevTraffic) * 100
            );
          }
        }

        monthlyData[month] = {
          traffic,
          percentage: this.getTrafficPercentageFromTotal(traffic),
          difference,
        };
      });

      return monthlyData;
    },

    getTrendCategory(keyword) {
      const monthlyData = this.getKeywordMonthlyDataWithDifference(keyword);
      const months = Object.keys(monthlyData).sort((a, b) => {
        // Ensure chronological order by sorting by year-month
        const [yearA, monthA] = a.split("-").map(Number);
        const [yearB, monthB] = b.split("-").map(Number);
        return yearA - yearB || monthA - monthB;
      });

      if (months.length < 2) return "Holding";

      const firstValue = monthlyData[months[0]].traffic;
      const lastValue = monthlyData[months[months.length - 1]].traffic;
      const totalChange = lastValue - firstValue;
      const percentageChange =
        firstValue > 0 ? (totalChange / firstValue) * 100 : 0;

      if (percentageChange > 10) return "Rising";
      if (percentageChange < -10) return "Falling";
      return "Holding";
    },

    getTrendCategoryColor(trend) {
      const colors = {
        Rising: "text-green-600",
        Falling: "text-red-600",
        Holding: "text-gray-600",
      };
      return colors[trend] || "text-gray-600";
    },
  },
  mounted() {
    this.startMonthYearOptions = this.generateMonthYearOptions();
    this.endMonthYearOptions = this.generateMonthYearOptions(true);

    // Load data sequentially to avoid loading state conflicts
    this.initializeData();

    const cachedStartMonthYear = sessionStorage.getItem(
      "gmbsearchReportStartMonthYear"
    );
    const cachedEndMonthYear = sessionStorage.getItem(
      "gmbsearchReportEndMonthYear"
    );
    const cachedSelectedStore = sessionStorage.getItem(
      "gmbsearchReportSelectedStore"
    );
    const cachedReportData = sessionStorage.getItem("gmbsearchReportData");
    const cachedLastGeneratedAt = sessionStorage.getItem(
      "gmbsearchReportLastGeneratedAt"
    );

    if (cachedStartMonthYear) {
      this.selectedStartMonthYear = JSON.parse(cachedStartMonthYear);
    }
    if (cachedEndMonthYear) {
      this.selectedEndMonthYear = JSON.parse(cachedEndMonthYear);
    }
    if (cachedSelectedStore) {
      this.selectedStore = JSON.parse(cachedSelectedStore);
      this.locationSearch =
        this.selectedStore.sub_locality ||
        this.selectedStore.gmbProfile?.title ||
        "";
      this.selectedWID = this.selectedStore.wid;
    }
    if (cachedReportData) {
      this.reportData = JSON.parse(cachedReportData);
    }
    if (cachedLastGeneratedAt) {
      this.lastGeneratedAt = this.formatTimeAgo(cachedLastGeneratedAt);
    }

    this.updateDateRangeDisplay();
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    if (this.categoryChart) {
      this.categoryChart.destroy();
    }
    if (this.totalTrafficTrendChart) {
      this.totalTrafficTrendChart.destroy();
    }
    Object.values(this.keywordTrendCharts).forEach((chart) => {
      if (chart) {
        chart.destroy();
      }
    });
    Object.values(this.categoryTrendCharts).forEach((chart) => {
      if (chart) {
        chart.destroy();
      }
    });
  },
  watch: {
    selectedStartMonthYear(newValue) {
      this.endMonthYearOptions = this.generateMonthYearOptions(true);
      this.updateDateRangeDisplay();
    },
    selectedEndMonthYear() {
      this.updateDateRangeDisplay();
    },
    filteredBrandKeywords: {
      handler() {
        // Use setTimeout to ensure DOM has updated after filtering
        setTimeout(() => {
          this.reinitializeBrandKeywordCharts();
        }, 100);
      },
      deep: true,
    },
    selectedCategories: {
      handler() {
        // This will trigger the filteredBrandKeywords watcher
      },
    },
    selectedTrends: {
      handler() {
        // This will trigger the filteredBrandKeywords watcher
      },
    },
    keywordSearch: {
      handler() {
        // This will trigger the filteredBrandKeywords watcher
      },
    },
  },
};
</script>

<style scoped>
/* Add any specific styles for this page */
.keyword-rank {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: #e5e7eb;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
}

.keywords-bars-container {
  max-width: 100%;
}

.keyword-bar-item {
  transition: all 0.2s ease;
}

.keyword-bar-item:hover {
  background-color: #f9fafb;
  border-radius: 4px;
  padding-left: 4px;
}

.high-volume {
  color: #2e7d32;
  font-weight: 600;
}

.medium-volume {
  color: #1976d2;
  font-weight: 500;
}

.low-volume {
  color: #757575;
  font-weight: 400;
}

/* Volume filter button styling */
.v-btn-toggle {
  display: flex;
  flex-wrap: wrap;
}

.v-btn-toggle .v-btn {
  margin: 2px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  height: auto !important;
}

.v-btn-toggle .v-btn.v-item--active {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

/* Location dropdown styling */
.location-input {
  width: 100% !important;
}

.platform {
  max-width: 100%;
}

.platform span {
  word-break: break-word;
  white-space: normal;
}

/* Add new styles for dashboard */
.v-tabs {
  width: 100%;
}

.v-tabs-items {
  width: 100%;
}

.v-window__container {
  width: 100%;
}

.v-window-item {
  width: 100%;
}

/* Add new styles */
.v-btn-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.v-btn-toggle .v-btn {
  margin: 0 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  height: auto !important;
  min-width: 100px;
}

.v-btn-toggle .v-btn.v-item--active {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

/* Improve table styles */
table {
  border-collapse: separate;
  border-spacing: 0;
}

th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f9fafb;
}

td,
th {
  padding: 12px 16px;
}

tr:hover {
  background-color: #f9fafb;
}

/* Improve card styles */
.bg-white {
  transition: all 0.2s ease;
}

.bg-white:hover {
  transform: translateY(-1px);
}

/* Add smooth transitions */
.transition-all {
  transition: all 0.2s ease;
}

/* Improve text field styles */
.v-text-field {
  border-radius: 8px;
}

/* Add responsive padding */
@media (max-width: 768px) {
  .p-6 {
    padding: 1rem;
  }

  .px-6 {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

/* Add styles for traffic percentage bars */
.traffic-bar {
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background-color: #e5e7eb;
}

.traffic-bar-fill {
  height: 100%;
  transition: width 0.3s ease;
}

/* Improve table styles */
td,
th {
  padding: 12px 16px;
  white-space: nowrap;
}

/* Add responsive styles for traffic bars */
@media (max-width: 768px) {
  .traffic-bar {
    width: 60px;
  }
}

/* Add these new styles */
.overflow-x-auto {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #edf2f7;
}

.overflow-x-auto::-webkit-scrollbar {
  height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #edf2f7;
  border-radius: 3px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 3px;
}

/* Update existing table styles */
table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}

th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f9fafb;
}

td,
th {
  padding: 12px 16px;
}

tr:hover {
  background-color: #f9fafb;
}

/* Add responsive styles */
@media (max-width: 640px) {
  .overflow-x-auto {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  td,
  th {
    padding: 8px 12px;
  }

  .w-24 {
    width: 16px;
  }
}

/* Add custom scrollbar styles */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #edf2f7;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #edf2f7;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: #a0aec0;
}

.custom-scrollbar .hover\:bg-gray-50:hover {
  background-color: #f7fafc;
  transition: background-color 0.2s ease;
}

/* Enhanced styles */
.bg-gray-50 {
  background-color: #f8fafc;
}

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #edf2f7;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #edf2f7;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: #a0aec0;
}

/* Add smooth transitions */
.transition-all {
  transition: all 0.2s ease;
}

/* Enhanced hover effects */
.hover\:shadow-sm:hover {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
}

/* Add gradient backgrounds for progress bars */
.bg-green-500 {
  background: linear-gradient(90deg, #48bb78 0%, #38a169 100%);
}

.bg-blue-500 {
  background: linear-gradient(90deg, #4299e1 0%, #3182ce 100%);
}

.bg-yellow-500 {
  background: linear-gradient(90deg, #ecc94b 0%, #d69e2e 100%);
}

.bg-gray-500 {
  background: linear-gradient(90deg, #a0aec0 0%, #718096 100%);
}

/* Add subtle animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}

/* Enhanced card styles */
.rounded-lg {
  border-radius: 0.75rem;
}

.shadow-sm {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Loading animation styles */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.loading-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-spin {
  animation: spin 1s linear infinite;
}

/* Enhanced loading states */
.v-progress-circular {
  transition: all 0.3s ease;
}

.v-progress-circular:hover {
  transform: scale(1.05);
}

/* Loading overlay styles */
.loading-overlay {
  backdrop-filter: blur(2px);
  transition: all 0.3s ease;
}

/* Disabled state styles */
.v-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.v-btn-toggle:disabled {
  opacity: 0.6;
  pointer-events: none;
}

/* Loading text animation */
@keyframes loadingDots {
  0%,
  20% {
    color: rgba(0, 0, 0, 0.87);
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  40% {
    color: rgba(0, 0, 0, 0.87);
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0.87), 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  60% {
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0.87), 0.5em 0 0 rgba(0, 0, 0, 0.87);
  }
  80%,
  100% {
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
  }
}

.loading-dots {
  animation: loadingDots 1.4s infinite;
}

/* Skeleton loading styles */
.skeleton-loading {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
